/**
 * Password Reset Page
 * Allows authenticated users to change their password
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NavBar } from "@/components/NavBar";
import { TwoFactorSettings } from "@/components/TwoFactorSettings";
import { toast } from "sonner";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";

export default function PasswordResetPage() {
  const navigate = useNavigate();
  const { token, setToken } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error("Password must contain at least one number");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (res.ok) {
        // Changing the password expires every older token, including this one
        const data = await res.json();
        localStorage.setItem("token", data.token);
        setToken(data.token);
        toast.success("Password changed successfully");
        navigate("/dashboard");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to change password");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Dashboard
        </Link>
      </NavBar>

      <main className="pt-24 pb-16 px-4 md:px-12">
        <div className="max-w-md mx-auto">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>

          <Card className="bg-white/[0.03] border-white/10 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-5 h-5 text-white/60" />
              <h1 className="text-2xl font-bold">Change Password</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-sm text-white/60">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                  required
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <Label className="text-sm text-white/60">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <Label className="text-sm text-white/60">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12"
                data-testid="change-password-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change Password"}
              </Button>
            </form>
          </Card>

          <TwoFactorSettings />
        </div>
      </main>
    </div>
  );
}
